import { useCallback, useMemo, type FC } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";

import { Button, DataTable, StatusBadge } from "../../shared";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { useComplianceRules } from "../../../queries/regulator-portal/compliance/useComplianceRules";
import { useStartComplianceManifest } from "../../../queries/regulator-portal/compliance/useStartComplianceManifest";
import type { ComplianceRule } from "../../../queries/regulator-portal/compliance/types";
import { buildRegulatorPortalRulePath } from "../regulatorPortalRoutes";
import { portalComplianceErrorMessage } from "./portalComplianceError";

import styles from "./Compliance.module.css";

const emptyValue = (value: string) => value || AppStrings.EMPTY_REASON;

const displayCount = (count: number) =>
  Number.isFinite(count) ? String(count) : AppStrings.EMPTY_REASON;

const RegulatorPortalRules: FC = () => {
  const navigate = useNavigate();
  const rulesQuery = useComplianceRules();
  const startManifest = useStartComplianceManifest();
  const rows = rulesQuery.data ?? [];
  const loadError =
    rulesQuery.isError && rows.length === 0
      ? portalComplianceErrorMessage(
          rulesQuery.error,
          AppStrings.PAM_REGULATOR_PORTAL_RULES_LOAD_ERROR,
        )
      : null;

  const columns = useMemo(
    () => [
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_CLIENT,
        dataIndex: "client",
        key: "client",
        render: (value: string, record: ComplianceRule) => (
          <button
            type="button"
            className={styles.rowLink}
            onClick={() =>
              navigate(buildRegulatorPortalRulePath(record.id), {
                state: { rule: record },
              })
            }
          >
            {emptyValue(value)}
          </button>
        ),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_REGULATOR_NAME,
        dataIndex: "regulator",
        key: "regulator",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.DESCRIPTION,
        dataIndex: "description",
        key: "description",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_MONITORED_WORKLOADS,
        dataIndex: "monitoredWorkloads",
        key: "monitoredWorkloads",
        render: (workloads: ComplianceRule["monitoredWorkloads"]) =>
          displayCount(Array.isArray(workloads) ? workloads.length : Number.NaN),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_MONITORED_DB_CONFIGS,
        dataIndex: "monitoredDbConfigs",
        key: "monitoredDbConfigs",
        render: (configs: ComplianceRule["monitoredDbConfigs"]) =>
          displayCount(Array.isArray(configs) ? configs.length : Number.NaN),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_GAME_STOPS,
        dataIndex: "indicators",
        key: "indicators",
        ellipsis: false,
        render: (indicators: string[]) =>
          indicators?.length ? (
            <div className={styles.tags}>
              {indicators.map((name) => (
                <StatusBadge key={name} tone="info" size="sm">
                  {name}
                </StatusBadge>
              ))}
            </div>
          ) : (
            AppStrings.EMPTY_REASON
          ),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_TARGET_ACCOUNT,
        dataIndex: "targetAccountId",
        key: "targetAccountId",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_BASELINE_ACCOUNT,
        dataIndex: "baselineAccountId",
        key: "baselineAccountId",
        render: (value: string) => emptyValue(value),
      },
    ],
    [navigate],
  );

  const handleStartManifest = useCallback(async () => {
    try {
      await startManifest.mutateAsync();
      message.success(AppStrings.PAM_REGULATOR_PORTAL_MANIFEST_SUCCESS);
    } catch (error) {
      message.error(
        portalComplianceErrorMessage(
          error,
          AppStrings.PAM_REGULATOR_PORTAL_MANIFEST_ERROR,
        ),
      );
    }
  }, [startManifest]);

  return (
    <section className={styles.page} data-testid="regulator-portal-rules">
      <div className={styles.header}>
        <h1 className={styles.title}>{AppStrings.PAM_REGULATOR_PORTAL_RULES}</h1>
        <Button
          onClick={handleStartManifest}
          loading={startManifest.isPending}
          data-testid="regulator-portal-start-manifest"
        >
          {AppStrings.PAM_REGULATOR_PORTAL_START_MANIFEST}
        </Button>
      </div>
      {loadError ? (
        <p className={styles.message} role="alert">
          {loadError}
        </p>
      ) : null}
      {rulesQuery.isLoading ? (
        <p className={styles.message}>{AppStrings.PAM_REGULATOR_PORTAL_LOADING}</p>
      ) : null}
      {!rulesQuery.isLoading && !loadError && rows.length === 0 ? (
        <p className={styles.message}>{AppStrings.PAM_REGULATOR_PORTAL_RULES_EMPTY}</p>
      ) : null}
      {!rulesQuery.isLoading && rows.length > 0 ? (
        <div className={styles.tableSection}>
          <DataTable<ComplianceRule>
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={rulesQuery.isFetching && rows.length === 0}
            pagination={false}
            scrollPreset="ultraWide"
            data-testid="regulator-portal-rules-table"
          />
        </div>
      ) : null}
    </section>
  );
};

export default RegulatorPortalRules;
